import { type Dayjs } from 'dayjs';
import { type ReactElement, useCallback, useMemo, useState } from 'react';
import { formatRangeWindowNoDataText } from '@/features/dashboard/hooks';
import { PipelinesTable } from '@/features/pipelines/components/PipelinesTable';
import { queryPipelines } from '@/features/pipelines/data';
import {
  pipelineSortFieldMap,
  type PipelineTableStatus,
  type PipelineSortDirection,
  type PipelineSortKey,
  pipelineDefaultSortKey,
  pipelineDefaultSortDirection,
  pipelineDefaultColumnVisibilityModel,
} from '@/features/pipelines/pages/pipelinesTableDefinition';
import {
  PipelinesTableToolbar,
  type CloneDateQuickFilter,
} from '@/features/pipelines/pages/PipelinesTableToolbar';
import { useCloneDateWindow } from '@/features/shared/hooks/useCloneDateWindow';
import { resolveEntityTags } from '@/features/shared/tags';
import {
  buildInFilter,
  buildInFilterWithNull,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import { Panel } from '@/ui/composites/Panel';
import type { TableQueryMeta } from '@/features/shared/components/tableSystem/tableToolbar.helpers';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { GridColumnVisibilityModel } from '@/ui/primitives';
import type { PipelineListItemDto, QueryResult } from '@flowqa/shared';

type PipelinesTablePanelProps = {
  visible: boolean;
  selectedProjectId: string | null;
  selectedOrganisationId: string | null;
  deltasEnabled: boolean;
};

export const PipelinesTablePanel = ({
  visible,
  selectedProjectId,
  selectedOrganisationId,
  deltasEnabled,
}: PipelinesTablePanelProps): ReactElement | null => {
  const [tableDateQuickFilter, setTableDateQuickFilter] = useState<CloneDateQuickFilter>('7d');
  const [tableFromDate, setTableFromDate] = useState<Dayjs | null>(null);
  const [tableToDate, setTableToDate] = useState<Dayjs | null>(null);
  const [debouncedTableSearch, setDebouncedTableSearch] = useState('');
  const [tableColumnVisibilityModel, setTableColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(pipelineDefaultColumnVisibilityModel);
  const [tableSortKey, setTableSortKey] = useState<PipelineSortKey>(pipelineDefaultSortKey);
  const [tableSortDirection, setTableSortDirection] = useState<PipelineSortDirection>(
    pipelineDefaultSortDirection,
  );
  const [tableFilterStatuses, setTableFilterStatuses] = useState<PipelineTableStatus[]>([]);
  const [tableFilterTags, setTableFilterTags] = useState<string[]>([]);
  const [tableVisibleRows, setTableVisibleRows] = useState<PipelineListItemDto[]>([]);
  const [tableMeta, setTableMeta] = useState<TableQueryMeta<PipelineListItemDto> | null>(null);

  const tableDateWindow = useCloneDateWindow({
    dateQuickFilter: tableDateQuickFilter,
    fromDate: tableFromDate,
    toDate: tableToDate,
  });
  const tableAvailableTags = useMemo(() => {
    const tags = new Set<string>();
    tableVisibleRows.forEach((row) => {
      resolveEntityTags(row).forEach((tag) => {
        tags.add(tag);
      });
    });
    return Array.from(tags.values()).sort((left, right) => left.localeCompare(right));
  }, [tableVisibleRows]);
  const handleDebouncedTableSearchChange = useCallback((value: string) => {
    setDebouncedTableSearch(value);
  }, []);
  const tableBuildStatusFilters = useMemo<GridQueryInput['filters']>(() => {
    if (tableFilterStatuses.length === 0) return [];
    const hasNoBuilds = tableFilterStatuses.includes('none');
    const buildStatuses = tableFilterStatuses.filter(
      (status): status is Exclude<PipelineTableStatus, 'none'> => status !== 'none',
    );
    const values = hasNoBuilds
      ? ([null, ...buildStatuses] as Array<Exclude<PipelineTableStatus, 'none'> | null>)
      : buildStatuses;
    return buildInFilterWithNull('latestBuildStatus', values);
  }, [tableFilterStatuses]);
  const tableQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<PipelineListItemDto>> => {
      const toolbarFilters = mergeFilters(
        tableBuildStatusFilters,
        buildInFilter('tags', tableFilterTags),
      );
      const sortOverride: ApiSort = {
        field: pipelineSortFieldMap[tableSortKey],
        direction: tableSortDirection,
      };
      return queryPipelines(selectedProjectId, selectedOrganisationId ?? undefined, {
        ...input,
        sorts: [sortOverride],
        filters: [...input.filters, ...toolbarFilters],
        metricsStartDay: tableDateWindow.startDay ?? undefined,
        metricsEndDay: tableDateWindow.endDay ?? undefined,
        previousMetricsStartDay: tableDateWindow.previousStartDay ?? undefined,
        previousMetricsEndDay: tableDateWindow.previousEndDay ?? undefined,
        include: {
          latestBuild: true,
          metrics: true,
          metricDeltas: deltasEnabled,
        },
      });
    },
    [
      tableBuildStatusFilters,
      tableDateWindow.endDay,
      tableDateWindow.previousEndDay,
      tableDateWindow.previousStartDay,
      tableDateWindow.startDay,
      tableFilterTags,
      tableSortDirection,
      tableSortKey,
      deltasEnabled,
      selectedOrganisationId,
      selectedProjectId,
    ],
  );
  const tableNoRowsText = tableDateWindow.hasCustomBounds
    ? 'No pipelines found for selected date range'
    : formatRangeWindowNoDataText('No pipelines found', tableDateQuickFilter, false);
  const tableStatusFilterKey = useMemo(
    () => [...tableFilterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
    [tableFilterStatuses],
  );
  const tableTagFilterKey = useMemo(
    () => [...tableFilterTags].sort((left, right) => left.localeCompare(right)).join(','),
    [tableFilterTags],
  );

  if (!visible) return null;

  return (
    <Panel padding="md" gap="none" contentGap="md" sx={{ backgroundColor: 'background.paper' }}>
      <PipelinesTableToolbar
        onDebouncedSearchChange={handleDebouncedTableSearchChange}
        columnVisibilityModel={tableColumnVisibilityModel}
        onColumnVisibilityModelChange={setTableColumnVisibilityModel}
        sortKey={tableSortKey}
        sortDirection={tableSortDirection}
        onSortChange={(nextSortKey, nextSortDirection) => {
          setTableSortKey(nextSortKey);
          setTableSortDirection(nextSortDirection);
        }}
        filterStatuses={tableFilterStatuses}
        onFilterStatusesChange={setTableFilterStatuses}
        filterTags={tableFilterTags}
        onFilterTagsChange={setTableFilterTags}
        dateQuickFilter={tableDateQuickFilter}
        onDateQuickFilterChange={(next) => {
          setTableDateQuickFilter(next);
          setTableFromDate(null);
          setTableToDate(null);
        }}
        fromDate={tableFromDate}
        toDate={tableToDate}
        onFromDateChange={setTableFromDate}
        onToDateChange={setTableToDate}
        availableTags={tableAvailableTags}
        summaryCount={tableMeta?.total ?? null}
      />
      <PipelinesTable
        persistKey="pipelines-table-clone"
        queryKey={[
          'pipelines-table-clone',
          tableDateQuickFilter,
          selectedOrganisationId ?? null,
          selectedProjectId ?? null,
          deltasEnabled ? 'deltas-on' : 'deltas-off',
          tableSortKey,
          tableSortDirection,
          tableStatusFilterKey,
          tableTagFilterKey,
          tableDateWindow.startDay,
          tableDateWindow.endDay,
        ]}
        queryFn={tableQueryFn}
        pageSizeOptions={[10]}
        initialPageSize={10}
        disablePageSizeSelect
        showDeltas={deltasEnabled}
        noRowsText={tableNoRowsText}
        showToolbar={false}
        disableColumnSorting
        disableColumnMenu
        search={debouncedTableSearch}
        onRowsChange={setTableVisibleRows}
        onQueryMeta={setTableMeta}
        columnVisibilityModel={tableColumnVisibilityModel}
        onColumnVisibilityModelChange={(nextModel) => {
          setTableColumnVisibilityModel({
            ...nextModel,
            name: true,
          });
        }}
      />
    </Panel>
  );
};
